import { getMimeTypeFromUrl } from '#pplx/frontend/common/files/urls.js';
const MAX_DIMENSION = 1080;
// AWS Rekognition rejects images where either side exceeds 10,000px.
const MAX_SIDE = 10_000;
const SVG_MIME_TYPE = 'image/svg+xml';
export function isImage(arg) {
    if (typeof arg === 'string') {
        const mimeType = getMimeTypeFromUrl(arg);
        return mimeType.startsWith('image/');
    }
    else {
        return arg.type.startsWith('image/');
    }
}
export function isSvg(arg) {
    if (typeof arg === 'string') {
        const mimeType = getMimeTypeFromUrl(arg);
        return mimeType === SVG_MIME_TYPE;
    }
    return arg.type === SVG_MIME_TYPE;
}
/**
 * Calculate new image dimensions where the smaller dimension equals the target dimension,
 * preserving the original aspect ratio. Only downscales if the smaller dimension is already larger than target.
 * An upper bound on the largest side is enforced (Rekognition's 10k limit).
 *
 * @param srcWidth - The original width of the image.
 * @param srcHeight - The original height of the image.
 * @param minDimension - The target size for the smaller dimension of the resized image.
 * @returns An object containing the new width and height.
 */
const calculateDimensionsWithMin = (srcWidth, srcHeight, minDimension) => {
    const minSrcDimension = Math.min(srcWidth, srcHeight);
    // Only resize if the smaller dimension is larger than target (downscale only)
    let width = srcWidth;
    let height = srcHeight;
    if (minSrcDimension > minDimension) {
        const ratio = minDimension / minSrcDimension;
        width = Math.round(srcWidth * ratio);
        height = Math.round(srcHeight * ratio);
    }
    // Enforce max side (downscale only)
    const maxDimension = Math.max(width, height);
    if (maxDimension > MAX_SIDE) {
        const ratio = MAX_SIDE / maxDimension;
        width = Math.max(1, Math.round(width * ratio));
        height = Math.max(1, Math.round(height * ratio));
    }
    return { width, height };
};
/**
 * Resize an image file so that its smaller dimension equals the specified target,
 * while maintaining the original aspect ratio. Only downscales, never upscales.
 *
 * @param file - The original image file to resize.
 * @param minDimension - The target size for the smaller dimension (default: MAX_DIMENSION).
 * @param quality - The image quality for JPEG conversion (0.0 to 1.0, default: 0.98).
 * @returns A Promise that resolves to a new File containing the resized image.
 */
export const resizeImageToMaxDimension = (file, minDimension = MAX_DIMENSION, quality = 0.98) => {
    return new Promise((resolve, reject) => {
        if (!isImage(file)) {
            reject(new Error('File is not an image.'));
            return;
        }
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d', {
            alpha: true, // Enable alpha channel for transparency
            colorSpace: 'srgb', // Use sRGB color space for better color accuracy
        });
        const image = new Image();
        const url = URL.createObjectURL(file);
        if (!context) {
            reject(new Error('Could not get canvas context'));
            return;
        }
        // Set high-quality rendering options
        context.imageSmoothingEnabled = true;
        context.imageSmoothingQuality = 'high';
        image.onload = () => {
            const newDimensions = calculateDimensionsWithMin(image.width, image.height, minDimension);
            // Set canvas dimensions to the calculated size and draw
            // resized image maintaining aspect ratio
            canvas.width = newDimensions.width;
            canvas.height = newDimensions.height;
            // Convert all formats to JPEG for consistency
            const outputMimeType = 'image/jpeg';
            const outputQuality = quality;
            // Fill with white background for JPEG (no transparency support)
            context.fillStyle = '#fff';
            context.fillRect(0, 0, canvas.width, canvas.height);
            context.drawImage(image, 0, 0, newDimensions.width, newDimensions.height);
            // Convert to blob and create new file with resized content
            canvas.toBlob((blob) => {
                if (!blob) {
                    reject(new Error('Failed to create blob from canvas'));
                    return;
                }
                const originalName = file.name;
                const newName = originalName.replace(/\.[^/.]+$/, '') + '.jpg';
                const resizedFile = new File([blob], newName, {
                    type: outputMimeType,
                });
                URL.revokeObjectURL(url);
                resolve(resizedFile);
            }, outputMimeType, outputQuality);
        };
        image.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error('Failed to load image.'));
        };
        image.src = url;
    });
};
//# sourceMappingURL=images.js.map